import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import parse from "html-react-parser";

import ProTip from "../../../ProTip";
import classes from "./formInfo.module.css";
import {
  errorDark,
  errorLight,
  white,
} from "../../../../theme/color.constants";

function FormInfo(props) {
  const { fieldProps } = props;
  const { key, content, cssClass, tags, label, link, isExternalLink } =
    fieldProps;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { highlight, center } = classes;
  const style = clsx({
    [classes["text-note-info-panel"]]: cssClass?.includes(
      "text-note-info-panel"
    ),
    [classes["text-note-error-panel"]]: cssClass?.includes(
      "text-note-error-panel"
    ),
    [classes["text-note-warning-panel"]]: cssClass?.includes(
      "text-note-warning-panel"
    ),
    [classes["text-note-left-red-small"]]: cssClass?.includes(
      "text-note-left-red-small"
    ),
    [classes["text-note-left-red-medium"]]: cssClass?.includes(
      "text-note-left-red-medium"
    ),
    [classes["text-note-info-panel-dark"]]:
      isDarkMode && cssClass?.includes("text-note-info-panel"),
    [highlight]: cssClass?.includes("highlight"),
    [center]: cssClass?.includes("center"),
  });

  const parsedContent = content ? parse(content) : null;

  const isInfo = cssClass?.includes("text-note-info");
  const isInfoDark = cssClass?.includes("text-note-info-panel-dark");
  const isWarning = cssClass?.includes("text-note-warning");
  const isError = cssClass?.includes("text-note-left-red");
  const isErrorPanel = cssClass?.includes("text-note-error-panel");

  const getColor = () => {
    if (isInfoDark) return isDarkMode ? white : "#254250";
    if (isInfo) return isDarkMode ? white : "rgb(1, 67, 97)";
    if (isWarning) return isDarkMode ? white : "rgb(102, 60, 0)";
    if (isError) return isDarkMode ? errorDark : errorLight;
    if (isErrorPanel) return isDarkMode ? white : errorLight;
    return undefined;
  };

  const getBgColor = () => {
    if (isInfoDark) return isDarkMode ? "#233a44" : "#b2c3ca";
    if (isInfo) return isDarkMode ? "#233a44" : "rgb(229, 246, 253)";
    if (isWarning) return isDarkMode ? "rgb(68 56 38)" : "rgb(255, 244, 229)";
    if (isErrorPanel) return isDarkMode ? "#4d2c2c" : "#fddede";
    return "none";
  };

  return link ? (
    <Box sx={{ margin: "1rem 0.5rem" }}>
      <Typography tabIndex="0" sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
        {label}: {tags && tags.includes("proTip") && <ProTip fieldKey={key} />}
        <Link
          sx={{ fontWeight: "normal" }}
          href={isExternalLink ? link : `${process.env.PUBLIC_URL}/${link}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
        >
          {content}
        </Link>
      </Typography>
    </Box>
  ) : (
    <Typography
      tabIndex="0"
      className={style}
      sx={{ color: getColor(), bgcolor: getBgColor() }}
    >
      {parsedContent}{" "}
      {tags && tags.includes("proTip") && <ProTip fieldKey={key} />}
    </Typography>
  );
}
FormInfo.propTypes = {
  fieldProps: PropTypes.shape({
    conditional: PropTypes.shape({
      when: PropTypes.string.isRequired,
      eq: PropTypes.string.isRequired,
      show: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    }),
    type: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    validate: PropTypes.shape({
      required: PropTypes.bool,
    }),
    cssClass: PropTypes.string,
    link: PropTypes.string,
    isExternalLink: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};
export default FormInfo;
